/**
 * MergeAllDialog — bulk "Merge all by email / Cisco ID" with a preview of
 * every group and a 24-hour undo. Reused by the Duplicates tool and the
 * Data Health card.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitMerge, Loader2, Undo2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

export function UndoMergeBar({ pending, onUndone }) {
  const { post } = useApi();
  const [busy, setBusy] = useState(false);
  if (!pending?.length) return null;
  const p = pending[0];
  const undo = async () => {
    setBusy(true);
    try {
      const r = await post(`/trainees/duplicates/undo/${p.undo_id}`, {});
      toast.success(`Merge undone — ${r.restored} records restored`);
      onUndone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Undo failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 text-sm" data-testid="merge-undo-bar">
      <span className="text-muted-foreground">
        Last merge ({p.result?.people ?? 0} {p.result?.people === 1 ? 'person' : 'people'}, {p.result?.removed ?? 0} record{p.result?.removed === 1 ? '' : 's'}) can be undone until{' '}
        {new Date(p.expires_at).toLocaleString()}.
      </span>
      <Button size="sm" variant="outline" onClick={undo} disabled={busy} data-testid="merge-undo-btn">
        <Undo2 className="w-4 h-4 mr-1" /> Undo merge
      </Button>
    </div>
  );
}

export default function MergeAllDialog({ open, onOpenChange, onDone }) {
  const { get, post } = useApi();
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHealth(null);
    get('/trainees/duplicates/health').then(setHealth).catch(() => setHealth({ duplicate_groups: 0, preview: [] }));
  }, [open, get]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await post('/trainees/duplicates/merge-all', {});
      toast.success(`${r.people} ${r.people === 1 ? 'person' : 'people'} consolidated, ${r.removed} record${r.removed === 1 ? '' : 's'} removed — undo available for 24 hours`);
      onOpenChange(false);
      onDone?.(r);
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge all failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="merge-all-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge className="w-5 h-5" /> Merge all duplicates</DialogTitle>
          <DialogDescription>
            Records sharing an email or Cisco/Employee ID become one person. The oldest record keeps its ID;
            every batch keeps its own status and scores. You can undo this for 24 hours.
          </DialogDescription>
        </DialogHeader>
        {!health && <p className="text-sm text-muted-foreground py-4">Scanning…</p>}
        {health && health.duplicate_groups === 0 && (
          <p className="text-sm text-muted-foreground py-4" data-testid="merge-all-clean">No duplicates — everything is clean.</p>
        )}
        {health && health.duplicate_groups > 0 && (
          <div className="space-y-2" data-testid="merge-all-preview">
            <p className="text-sm"><strong>{health.duplicate_groups}</strong> {health.duplicate_groups === 1 ? 'person' : 'people'} · <strong>{health.duplicate_records}</strong> extra record{health.duplicate_records === 1 ? '' : 's'} will be removed</p>
            <div className="divide-y border rounded-md max-h-72 overflow-y-auto">
              {health.preview.map(g => (
                <div key={g.primary_id} className="p-2 text-xs" data-testid={`merge-all-group-${g.primary_id}`}>
                  <p className="font-medium text-sm">{g.name}</p>
                  {g.records.map((r, i) => (
                    <p key={r.trainee_id} className="text-muted-foreground">
                      {i === 0 ? '★ keeps ' : '→ folds '}{r.email || '—'} · {r.employee_id || '—'} · created {(r.created_at || '').slice(0, 10)}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={confirm} disabled={busy || !health || health.duplicate_groups === 0} data-testid="merge-all-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Merge {health?.duplicate_groups || ''} {health?.duplicate_groups === 1 ? 'person' : 'people'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
